import { useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { House, IdCard, X } from "lucide-react";
import { toast } from "react-toastify";
import { updateLandlordTenantRequirements } from "../context/functions/AdminFunction";
import {
  SCOPE_LABELS,
  TENANT_REQUIREMENTS,
  normalizeTenantRequirements,
} from "../utils/tenantRequirements";

const REQUIREMENT_ICONS = {
  household: <House />,
  client_identification: <IdCard />,
};

const joinLabels = (labels) => labels.join(", ");

// Lines for "What tenants will see", built from the current switches
const buildTenantSummary = (requirements) => {
  const active = TENANT_REQUIREMENTS.filter(
    ({ key }) => requirements[key].active,
  );
  const beforeSharing = active
    .filter(({ key }) => requirements[key].required_before_sharing)
    .map(({ shortLabel, scope }) =>
      scope === "person"
        ? `${shortLabel} (only the person who shares)`
        : shortLabel,
    );
  const afterSharing = active
    .filter(
      ({ key, scope }) =>
        scope === "application" && !requirements[key].required_before_sharing,
    )
    .map(({ shortLabel }) => shortLabel);
  const personSteps = active.filter(({ scope }) => scope === "person");
  const pendingForEachMember = personSteps
    .filter(({ key }) => !requirements[key].required_before_sharing)
    .map(({ shortLabel }) => shortLabel);
  const pendingForOtherMembers = personSteps
    .filter(({ key }) => requirements[key].required_before_sharing)
    .map(({ shortLabel }) => shortLabel);

  const lines = [];
  if (beforeSharing.length) {
    lines.push({
      tone: "before",
      title: "Before sharing:",
      text: joinLabels(beforeSharing),
    });
  }
  if (afterSharing.length) {
    lines.push({
      tone: "after",
      title: "After sharing:",
      text: joinLabels(afterSharing),
    });
  }
  if (pendingForEachMember.length) {
    lines.push({
      tone: "after",
      title: "After sharing, pending for each member:",
      text: joinLabels(pendingForEachMember),
    });
  }
  if (pendingForOtherMembers.length) {
    lines.push({
      tone: "after",
      title: "After sharing, pending for the other members:",
      text: joinLabels(pendingForOtherMembers),
    });
  }
  return lines;
};

// Same switch as the landlord table settings
const RequirementSwitch = ({ checked, label, disabled, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    className={`landlord-setting-switch${checked ? " is-on" : ""}`}
    disabled={disabled}
    onClick={() => onChange(!checked)}
  >
    <span className="landlord-setting-switch-thumb" />
  </button>
);

const TenantRequirementsModal = ({ show, landlord, onHide, onSave }) => {
  const [requirements, setRequirements] = useState(() =>
    normalizeTenantRequirements(landlord?.tenant_requirements),
  );
  const [saving, setSaving] = useState(false);

  const updateRequirement = (key, field, value) => {
    setRequirements((current) =>
      normalizeTenantRequirements({
        ...current,
        [key]: { ...current[key], [field]: value },
      }),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await updateLandlordTenantRequirements(
        landlord._id,
        requirements,
      );
      toast.success("Tenant requirements updated");
      onSave?.(response?.tenant_requirements ?? requirements);
      onHide();
    } catch (error) {
      console.log(error);
      toast.error(
        error?.response?.data?.message ||
          "Error while updating tenant requirements",
      );
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    if (!saving) onHide();
  };

  const summaryLines = buildTenantSummary(requirements);
  const landlordName =
    `${landlord?.fname_companyName || ""} ${landlord?.lname || ""}`.trim();

  return (
    <Modal
      show={show}
      centered
      dialogClassName="tenant-req-dialog"
      className="tenant-req-modal"
      onHide={close}
      aria-labelledby="tenant-req-title"
    >
      <div className="tenant-req-header">
        <div>
          <h2 id="tenant-req-title" className="tenant-req-title">
            Tenant requirements
          </h2>
          {landlordName && (
            <div className="tenant-req-subtitle">
              {landlordName} · workspace
            </div>
          )}
        </div>
        <button
          type="button"
          className="tenant-req-close"
          aria-label="Close"
          onClick={close}
        >
          <X />
        </button>
      </div>

      <div className="tenant-req-body">
        <p className="tenant-req-intro">
          Extra steps tenants complete for this landlord. They appear in the
          Landlord next steps modal.
        </p>

        {TENANT_REQUIREMENTS.map((requirement) => {
          const { active, required_before_sharing } =
            requirements[requirement.key];

          return (
            <div className="tenant-req-card" key={requirement.key}>
              <div className="tenant-req-main">
                <span className="tenant-req-icon" aria-hidden="true">
                  {REQUIREMENT_ICONS[requirement.key]}
                </span>
                <div className="tenant-req-content">
                  <div className="tenant-req-row">
                    <div className="tenant-req-copy">
                      <div className="tenant-req-label">
                        {requirement.label}
                      </div>
                      <div className="tenant-req-description">
                        {requirement.description}
                      </div>
                      <span className="tenant-req-scope">
                        {SCOPE_LABELS[requirement.scope]}
                      </span>
                    </div>
                    <div className="tenant-req-toggle">
                      <span>Active</span>
                      <RequirementSwitch
                        checked={active}
                        label={`${requirement.label}: active`}
                        disabled={saving}
                        onChange={(value) =>
                          updateRequirement(requirement.key, "active", value)
                        }
                      />
                    </div>
                  </div>

                  {active && (
                    <div className="tenant-req-row tenant-req-subrow">
                      <div className="tenant-req-copy">
                        <div className="tenant-req-sublabel">
                          Required before sharing
                        </div>
                        <div className="tenant-req-description">
                          {requirement.beforeSharingHint}
                        </div>
                      </div>
                      <RequirementSwitch
                        checked={required_before_sharing}
                        label={`${requirement.label}: required before sharing`}
                        disabled={saving}
                        onChange={(value) =>
                          updateRequirement(
                            requirement.key,
                            "required_before_sharing",
                            value,
                          )
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        <div className="tenant-req-summary">
          <div className="tenant-req-summary-title">What tenants will see</div>
          {summaryLines.length === 0 ? (
            <div className="tenant-req-summary-line">
              No extra steps. Tenants share as usual.
            </div>
          ) : (
            summaryLines.map((line) => (
              <div className="tenant-req-summary-line" key={line.title}>
                <span className={`tenant-req-dot is-${line.tone}`} />
                <span>
                  <strong>{line.title}</strong> {line.text}.
                </span>
              </div>
            ))
          )}
        </div>

        <p className="tenant-req-note">
          Applications already shared with this landlord get the active steps as
          pending.
        </p>
      </div>

      <div className="tenant-req-footer">
        <Button
          variant="light"
          className="tenant-req-cancel"
          disabled={saving}
          onClick={close}
        >
          Cancel
        </Button>
        <Button
          className="tenant-req-save"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </div>
    </Modal>
  );
};

export default TenantRequirementsModal;
